"use client";

import React, { useState } from "react";
import { LucideIcon, ChevronDown, ChevronUp } from "lucide-react";
import { Typography } from "./Typography";
import { Button } from "@/components/forms/Button";
import { Card } from "./Card";
import { cn } from "@/lib/utils/cn";

export interface SectionCardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string | React.ReactNode;
  headerPrefix?: React.ReactNode;
  icon?: LucideIcon;
  headerAction?: React.ReactNode;
  badge?: React.ReactNode;
  footer?: React.ReactNode;
  variant?: "default" | "bordered" | "elevated";
  padding?: "none" | "sm" | "md" | "lg";
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /**
   * Drive the open state from the caller; omit for the uncontrolled behaviour every existing
   * consumer relies on. Pass with `onOpenChange` when the state must outlive the component.
   *
   * This card deliberately knows nothing about storage — a shared card writing to a key of its
   * own would collide the moment two rendered on one page.
   */
  open?: boolean;
  /** Fired with the state the user asked for. Required for `open` to be actionable. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Hide the collapsed body with CSS instead of unmounting it. Default `false`, which is
   * the historical behaviour and stays the default for every existing consumer.
   *
   * Opt in when the content owns state that must survive a collapse — an in-flight request,
   * a queue or a debounce held in a ref, a part-typed field. `AdditionalCoveragesTile` keeps
   * a coverage write queue in a ref, and a user can collapse the card a moment after
   * clicking a toggle; unmounting there would drop whatever is still queued.
   *
   * The subtree keeps rendering while hidden, so this is only worth it for content that is
   * genuinely stateful.
   */
  keepMounted?: boolean;
}

const variantStyles = {
  default: "",
  bordered: "border border-gray-200",
  elevated: "shadow-lg border-gray-100",
};

const paddingStyles = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

export function SectionCard({
  children,
  title,
  subtitle,
  headerPrefix,
  icon: Icon,
  headerAction,
  badge,
  footer,
  variant = "default",
  padding = "md",
  className = "",
  headerClassName = "",
  contentClassName = "",
  collapsible = false,
  defaultOpen = true,
  open,
  onOpenChange,
  keepMounted = false,
}: SectionCardProps) {
  // Controlled only when `open` is actually supplied, so the uncontrolled path is
  // byte-for-byte what it was before this prop existed.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  // One toggle for both chevron and header click, so the two cannot drift apart.
  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const paddingClass = paddingStyles[padding];
  const hasHeader = title || subtitle || Icon || headerAction || badge;

  return (
    <Card
      padding="none"
      className={cn(variantStyles[variant], className)}
    >
      {hasHeader && (
        <div
          className={cn(
            "flex items-center justify-between gap-4 border-b border-gray-200",
            padding === "none" ? "p-6" : paddingClass,
            collapsible && "cursor-pointer hover:bg-gray-50 transition-colors",
            // Collapsed, the body is `hidden` (zero height), so the header's bottom rule lands
            // 1px above `Card`'s own border and reads as a doubled line.
            collapsible && !isOpen && "border-b-0",
            headerClassName,
          )}
          onClick={collapsible ? toggle : undefined}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {Icon && (
              <div className="flex-shrink-0 rounded-xl bg-gradient-to-br from-navigator-primary to-navigator-primary-dark flex items-center justify-center p-2.5">
                <Icon className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {headerPrefix && (
                <div className="mb-2">{headerPrefix}</div>
              )}
              {title && (
                <Typography variant="headline3" as="h3" truncate>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="headline5" color="muted" as="div" className="mt-0.5">
                  {typeof subtitle === "string" ? <p>{subtitle}</p> : subtitle}
                </Typography>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {badge && <div>{badge}</div>}
            {headerAction && (
              <div onClick={(e) => e.stopPropagation()}>{headerAction}</div>
            )}
            {collapsible && (
              <Button
                type="button"
                iconOnly
                startIcon={isOpen ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
                variant="ghost"
                size="md"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle();
                }}
                title={isOpen ? "Collapse" : "Expand"}
                aria-label={isOpen ? "Collapse section" : "Expand section"}
                className="bg-gray-50 border border-gray-200 shadow-sm hover:bg-gray-100 hover:border-gray-300"
              />
            )}
          </div>
        </div>
      )}

      {(!collapsible || isOpen || keepMounted) && (
        <div
          // `hidden` rather than dropping the node, when the caller asked to keep it
          // mounted. See `keepMounted` above.
          hidden={collapsible && !isOpen}
          className={cn(paddingClass, contentClassName)}
        >
          {children}
        </div>
      )}

      {footer && (
        <div
          className={cn(
            "border-t border-gray-200 bg-gray-50 rounded-b-xl",
            padding === "none" ? "p-6" : paddingClass,
          )}
        >
          {footer}
        </div>
      )}
    </Card>
  );
}
